<template>
  <v-card
    class="rounded-xl"
    elevation="0"
  >
    <v-card-title class="pa-6 pb-2">
      <div class="d-flex align-center">
        <v-icon
          icon="mdi-chart-line"
          color="primary"
          class="me-2"
        />
        <span class="text-h6 font-weight-medium">Range vs MCS</span>
      </div>
      <p class="text-body-2 text-medium-emphasis mt-1">
        Maximum achievable range (meters) at each MCS modulation index
      </p>
    </v-card-title>
    <v-card-text class="pa-6 pt-2">
      <div class="chart-container">
        <Line
          :data="chartData"
          :options="chartOptions"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useTheme } from 'vuetify'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type TooltipItem
} from 'chart.js'
import { Line } from 'vue-chartjs'
import type { IP2PGraphDataPoint } from '@/services/link_budget/link_budget.types'
import { filterAndSortMcsData } from '@/services/link_budget/mcs-filter'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

const mcsThroughputMap: Record<number, string> = {
  0: 'No Link',
  1: '330 Mbps',
  2: '660 Mbps',
  3: '820 Mbps',
  4: '1000 Mbps',
  5: '1090 Mbps',
  6: '1340 Mbps',
  7: '1650 Mbps',
  8: '1990 Mbps',
  9: '2130 Mbps',
  10: '2580 Mbps',
  11: '2960 Mbps',
  12: '3000 Mbps',
}

const theme = useTheme()

const primaryColor = computed(() => {
  const rgb = theme.current.value.colors.primary
  return rgb
})

const primaryColorAlpha = (alpha: number) => {
  const hex = primaryColor.value.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const props = defineProps<{
  data: IP2PGraphDataPoint[]
}>()

const chartData = computed(() => {
  const sorted = filterAndSortMcsData(props.data)

  return {
    labels: sorted.map(d => `MCS ${d.mcs}`),
    datasets: [
      {
        label: 'Range (m)',
        data: sorted.map(d => d.range),
        borderColor: primaryColor.value,
        backgroundColor: primaryColorAlpha(0.12),
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: primaryColor.value,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 1.5,
        tension: 0.3,
        fill: true,
      }
    ]
  }
})

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y' as const,
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        title: (items: TooltipItem<'line'>[]) => items[0]?.label ?? '',
        label: (item: TooltipItem<'line'>) => {
          const mcs = filterAndSortMcsData(props.data)[item.dataIndex]?.mcs ?? item.dataIndex
          const throughput = mcsThroughputMap[mcs] ?? ''
          const range = item.parsed.x ?? 0
          return [
            ` Range: ${range.toLocaleString()} m`,
            ` Throughput: ${throughput}`,
          ]
        },
      },
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: 'Range (meters)',
        font: { weight: 'bold' as const },
      },
      grid: {
        color: 'rgba(var(--v-border-color), 0.08)',
      },
      ticks: {
        callback: (value: string | number) => `${Number(value).toLocaleString()} m`,
      },
      beginAtZero: true,
    },
    y: {
      title: {
        display: true,
        text: 'MCS Index',
        font: { weight: 'bold' as const },
      },
      grid: {
        color: 'rgba(var(--v-border-color), 0.08)',
      },
    },
  },
}))
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 320px;
}
</style>
